import React, { useState, useEffect } from 'react';
import './styles.scss';
import { getOccupationsByArea } from '@/services/datasetService';

const AreaExplorerModal = ({ isOpen, onClose, datasetType, areaBreakdown }) => {
  const [selectedArea, setSelectedArea] = useState(null);
  const [occupations, setOccupations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Reset state when modal is closed or opened
  useEffect(() => {
    if (!isOpen) {
      setSelectedArea(null);
      setOccupations([]);
      setError(null);
      setSearchQuery('');
    }
  }, [isOpen]);

  const handleAreaClick = async (areaName) => {
    setSelectedArea(areaName);
    setSearchQuery('');
    setLoading(true);
    setError(null);
    try {
      const data = await getOccupationsByArea(datasetType, areaName);
      setOccupations(data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setSelectedArea(null);
    setSearchQuery('');
    setOccupations([]);
  };

  const filteredAreas = areaBreakdown?.filter(area => 
    area.name.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  const filteredOccupations = occupations.filter(occ => 
    (occ.occTitle || occ.occ_title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (occ.occCode || occ.occ_code || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (occ.naicsTitle || occ.naics || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="area-modal-overlay" onClick={onClose}>
      <div className="area-modal-content" onClick={e => e.stopPropagation()}>
        <div className="area-modal-header">
          <div className="header-left">
            {selectedArea && (
              <button className="back-btn" onClick={handleBack}>
                ← Back to Areas
              </button>
            )}
            <h3>
              {selectedArea 
                ? `Occupations in ${selectedArea}` 
                : `Geographic Coverage Overview`}
            </h3>
          </div>
          <div className="header-right" style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <input 
              type="text" 
              placeholder={selectedArea ? "Search occupations..." : "Search areas..."} 
              className="area-search-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button className="close-btn" onClick={onClose}>&times;</button>
          </div>
        </div>
        
        <div className="area-modal-body">
          {!selectedArea ? (
            <div className="area-directory-grid">
              {filteredAreas.length > 0 ? (
                filteredAreas.map((area, idx) => (
                  <div key={idx} className="area-directory-item" onClick={() => handleAreaClick(area.name)}>
                    <span className="area-name">{area.name}</span>
                    <span className="area-vol">{area.value > 0 ? area.value.toLocaleString() : '0'}</span>
                  </div>
                ))
              ) : (
                <div className="drill-down-loading">No geographic breakdown data available.</div>
              )}
            </div>
          ) : (
            <>
              {loading ? (
                <div className="drill-down-loading">Fetching fully comprehensive occupation list...</div>
              ) : error ? (
                <div className="drill-down-loading" style={{color: '#ef4444'}}>Error: {error}</div>
              ) : (
                <table className="drill-down-table">
                  <thead>
                    <tr>
                      <th>Occupation Title</th>
                      <th>SOC Code</th>
                      <th>Total Employment</th>
                      <th>Median Wage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOccupations.map((occ, idx) => (
                      <tr key={idx}>
                        <td>
                          <div className="occ-title">{occ.occTitle || occ.occ_title || 'Unknown Title'}</div>
                          {occ.naicsTitle && <div className="occ-meta">{occ.naicsTitle}</div>}
                        </td>
                        <td className="mono-val">{occ.occCode || occ.occ_code}</td>
                        <td className="mono-val">
                          {occ.totalEmployment || occ.tot_emp 
                            ? (occ.totalEmployment || occ.tot_emp).toLocaleString() 
                            : '—'}
                        </td>
                        <td className="mono-val" style={{color: '#10b981'}}>
                          {occ.annualMedian || occ.a_median 
                            ? `$${(occ.annualMedian || occ.a_median).toLocaleString()}` 
                            : '—'}
                        </td>
                      </tr>
                    ))}
                    {filteredOccupations.length === 0 && (
                      <tr>
                        <td colSpan="4" style={{textAlign: 'center', padding: '2rem'}}>No records match your search.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AreaExplorerModal;
